/**
 * MktgBodyText — reusable body paragraph for marketing sections.
 * Applies the .mktg-body base class (Sen font, 18px, weight 400,
 * line-height 1.5, dark color) plus optional size and color variant
 * classes defined in marketing-theme.css.
 */

import React from 'react';
import { cn } from '@/lib/utils/utils';

type BodyElement = 'p' | 'span' | 'div';

/** Size maps to .mktg-body-{size} CSS class; undefined = 18px base */
type BodySize = 'xl' | 'lg' | 'base' | 'sm' | 'xs' | '2xs';

/** Color maps to .mktg-body-{color} CSS class; 'dark' is the base default */
type BodyColor = 'dark' | 'gray' | 'white' | 'white-muted';

interface MktgBodyTextProps {
  as?: BodyElement;
  size?: BodySize;
  color?: BodyColor;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const sizeClass: Record<BodySize, string | undefined> = {
  xl: 'mktg-body-xl',
  lg: 'mktg-body-lg',
  base: undefined, // 18px is already in the base .mktg-body class
  sm: 'mktg-body-sm',
  xs: 'mktg-body-xs',
  '2xs': 'mktg-body-2xs'
};

const colorClass: Record<BodyColor, string | undefined> = {
  dark: undefined, // default in .mktg-body — no extra class needed
  gray: 'mktg-body-gray',
  white: 'mktg-body-white',
  'white-muted': 'mktg-body-white-muted'
};

export function MktgBodyText({
  as: Component = 'p',
  size = 'base',
  color = 'dark',
  className,
  style,
  children
}: MktgBodyTextProps) {
  return (
    <Component
      className={cn('mktg-body', sizeClass[size], colorClass[color], className)}
      style={style}
    >
      {children}
    </Component>
  );
}
